import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useBusinessContext } from '@context'
import { fetchPayment, IPaymentData } from '@query'
import { useTranslation } from 'react-i18next'
import { isPaymentInProgress, isPaymentPending, isPaymentRejected } from '@utils/pain.ts'
import styled from 'styled-components'
import { FaLandmark, FaTimesCircle } from 'react-icons/fa'

interface Props {
  paymentId: string
}

export const HolviPaymentStatusTracker: React.FC<Props> = ({ paymentId }) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()

  const { data: payment } = useQuery<IPaymentData>({
    queryKey: [slug, 'payments', paymentId],
    queryFn: () => fetchPayment({ slug, paymentId }),
    ...{
      enabled: !!paymentId,
      refetchInterval: query => {
        const paymentData = query.state.data
        return paymentData && (isPaymentPending(paymentData) || isPaymentInProgress(paymentData))
          ? 10000
          : false
      }
    }
  })

  if (!payment) return null

  if (isPaymentPending(payment) || isPaymentInProgress(payment)) {
    return (
      <StyledStatusContainer>
        <FaLandmark />
        <span>
          {isPaymentPending(payment)
            ? t('containers.paymentFormModal.paymentPending')
            : t('containers.paymentFormModal.paymentInProgress')}
        </span>
      </StyledStatusContainer>
    )
  }

  if (isPaymentRejected(payment)) {
    return (
      <StyledStatusContainer $error>
        <FaTimesCircle />
        <span>{t('containers.paymentFormModal.paymentFailed')}</span>
      </StyledStatusContainer>
    )
  }

  return null
}

const StyledStatusContainer = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  margin-bottom: 1.125rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  background: ${({ theme, $error }) =>
    $error ? `${theme.colors.error}14` : theme.colors.neutralGray};
  color: ${({ theme, $error }) => ($error ? theme.colors.error : theme.colors.menuBg)};

  svg {
    width: 0.95rem;
    height: 0.95rem;
    flex-shrink: 0;
  }
`
